// Offline cut compiler — a line-for-line port of backend/plotter.py (HP-GL / GP-GL / DMPL). Used ONLY when the laptop has no
// internet; online cuts are still compiled by the server. Knife paths (the spline rebuild in edges.py) are not ported: the offline
// bundle carries the server-built knife path for every outline and hole, and pieces pass it in as `knife` / `knife_holes`.
const CURVE_SPEED_PCT = 50, FEED_OUT_IN = 2.5, OVERCUT_IN = 0.03, TRAVEL_SPEED = 60, TRAVEL_MIN_IN = 4.0, EDGE_IN = 1.0, EDGE_IN_FAR = 0.5;

// Python round(): half to even.
const pyRound = (v) => { const f = Math.floor(v), d = v - f; if (d > 0.5) return f + 1; if (d < 0.5) return f; return f % 2 === 0 ? f : f + 1; };
const num = (v, dflt) => { const n = parseFloat(v); return Number.isFinite(n) ? n : dflt; };
const hyp = (dx, dy) => Math.hypot(dx, dy) || 1e-9;

function unit(a, b) { const dx = b[0] - a[0], dy = b[1] - a[1], l = hyp(dx, dy); return [dx / l, dy / l]; }

export function compensateOffset(P, d) {
  const n = P.length;
  if (n < 3 || d <= 0) return [...P, P[0]];
  const dirs = P.map((p, i) => unit(p, P[(i + 1) % n]));
  const out = [];
  for (let i = 0; i < n; i++) {
    const [tx, ty] = dirs[i], a = P[i], b = P[(i + 1) % n];
    out.push([a[0] + d * tx, a[1] + d * ty]);
    out.push([b[0] + d * tx, b[1] + d * ty]);
    const [ux, uy] = dirs[(i + 1) % n];
    const a0 = Math.atan2(ty, tx), a1 = Math.atan2(uy, ux);
    const turn = ((a1 - a0 + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
    const steps = Math.trunc(Math.abs(turn) / (12 * Math.PI / 180));
    for (let k = 1; k <= steps; k++) { const ang = a0 + turn * k / (steps + 1); out.push([b[0] + d * Math.cos(ang), b[1] + d * Math.sin(ang)]); }
  }
  const [tx, ty] = dirs[0];
  out.push([P[0][0] + (d + OVERCUT_IN) * tx, P[0][1] + (d + OVERCUT_IN) * ty]);
  return out;
}

const bbox = (pieces) => {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const pc of pieces) for (const [x, y] of pc.world_points) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
  return [x0, y0, x1, y1];
};
const length = (pieces, margin = 0) => bbox(pieces)[3] + margin;
// edge clearance (mirrors plotter._edge_in): across the film it shrinks so the far side keeps EDGE_IN_FAR inside the roll
export const edgeIn = (st = {}, pieces = null, rollWidth = null, margin = 0) => {
  let e = Math.max(0, Math.min(3, num(st.edge_in, EDGE_IN)));
  if (pieces?.length && rollWidth) { const b = bbox(pieces); e = Math.max(0, Math.min(e, rollWidth - EDGE_IN_FAR - (b[2] - b[0]) - 2 * margin)); }
  return e;
};
// machine X of the far end of the job (mirrors plotter._job_length)
const jobLength = (pieces, st, weedBox, margin) => length(pieces, weedBox ? margin / 2 : 0) + (st.offset_compensation ? 0.5 : 0) + edgeIn(st);
// cross_anchor "screen": null → the film width is the frame, the job is cut exactly where it sits on screen (Origin set at the film edge)
const edges = (pieces, margin = 0, st = {}, rollWidth = null) => { if (st.cross_anchor === "screen") return null; const b = bbox(pieces), e = edgeIn(st, pieces, rollWidth, margin); return [b[0] - margin - e, b[2] + margin + e]; };
const signs = (st = {}) => [Math.trunc(num(st.feed_dir, 1) || 1) < 0 ? -1 : 1, Math.trunc(num(st.cross_dir, -1) || -1) > 0 ? 1 : -1];

export function filmOffsetAfter(pieces, st = {}, weedBox = false, margin = 0.5) {
  if (st.feed_out === false || !pieces.length) return 0;
  return +(length(pieces, weedBox ? margin / 2 : 0) + edgeIn(st) - bbox(pieces)[1] + num(st.feed_out_in, FEED_OUT_IN)).toFixed(3);
}

function curveFlags(P) {
  const n = P.length;
  if (n < 3) return P.map(() => false);
  const turn = (i) => { const a = P[((i - 1) % n + n) % n], b = P[i % n], c = P[(i + 1) % n]; return Math.abs(Math.atan2((b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]), (b[0] - a[0]) * (c[0] - b[0]) + (b[1] - a[1]) * (c[1] - b[1])) * 180 / Math.PI); };
  const out = [];
  for (let i = 0; i < n; i++) { const t0 = turn(i), t1 = turn(i + 1); out.push(Math.hypot(P[(i + 1) % n][0] - P[i][0], P[(i + 1) % n][1] - P[i][1]) < 2.0 && ((0.15 < t0 && t0 < 20) || (0.15 < t1 && t1 < 20))); }
  return out;
}

// knife: server-built knife path in world coords (null for the weed box → raw points)
function pts(points, knife, upi, rollWidth, st, L, R) {
  const sl = num(st.scale_length, 1) || 1, sw = num(st.scale_width, 1) || 1;
  const [left, right] = R || [0, rollWidth];
  const [fs, cs] = signs(st);
  let src = knife || points, closedByCaller = true;
  if (knife && st.offset_compensation) { src = compensateOffset(src, num(st.blade_offset_mm, 0) / 25.4); closedByCaller = false; }
  const flags = knife ? curveFlags(src) : src.map(() => false);
  let out = [], fl = [];
  src.forEach(([x, y], i) => {
    const fx = L != null ? L - y : y;
    const q = [Math.max(0, pyRound(fx * upi * sl)), Math.max(0, pyRound((cs < 0 ? right - x : x - left) * upi * sw))];
    if (!out.length || q[0] !== out[out.length - 1][0] || q[1] !== out[out.length - 1][1]) { out.push(q); fl.push(flags[i]); }
  });
  if (closedByCaller && out.length > 2 && L != null) {
    let k = 0;
    for (let i = 1; i < out.length; i++) if (out[i][0] > out[k][0] || (out[i][0] === out[k][0] && -out[i][1] > -out[k][1])) k = i;
    out = [...out.slice(k), ...out.slice(0, k)]; fl = [...fl.slice(k), ...fl.slice(0, k)];
  }
  if (fs < 0) out = out.map(([x, y]) => [-x, y]);
  return [out, closedByCaller, fl];
}

const panel = (m, st) => Boolean(m.panel_conditions || st.use_panel);
const curveSpeed = (st, m) => { const speed = Math.trunc(num(st.speed, m.default_speed)); const pct = Math.trunc(num(st.curve_speed_pct, CURVE_SPEED_PCT)) || CURVE_SPEED_PCT; return [speed, st.curve_slowdown ? Math.max(1, pyRound(speed * pct / 100)) : speed]; };
// Mirror of plotter._travel_speed(): pen-up speed, or null when not faster than the cutting speed.
const travelSpeed = (st, m) => { if (st.drift_safe) return null; const fast = curveSpeed(st, m)[0]; const t = Math.trunc(num(st.travel_speed, TRAVEL_SPEED)) || TRAVEL_SPEED; return t > fast ? t : null; };
const feedOut = (pieces, upi, st) => signs(st)[0] * pyRound(filmOffsetAfter(pieces, { ...st, feed_out: true }) * upi * (num(st.scale_length, 1) || 1));
const weedBoxPts = (pieces, margin) => { const [x0, y0, x1, y1] = bbox(pieces), m = margin / 2; return [[x0 - m, y0 - m], [x1 + m, y0 - m], [x1 + m, y1 + m], [x0 - m, y1 + m], [x0 - m, y0 - m]]; };
const loopsOf = (pc, upi, rollWidth, st, L, R) => [...(pc.world_holes || []).map((h, i) => pts(h, (pc.knife_holes || [])[i] || null, upi, rollWidth, st, L, R)), pts(pc.world_points, pc.knife || null, upi, rollWidth, st, L, R)];

function genHpgl(pieces, m, st, weedBox, margin, rollWidth) {
  const upi = m.units_per_inch, speed = Math.trunc(num(st.speed, m.default_speed)), force = Math.trunc(num(st.force, m.default_force)), passes = Math.trunc(num(st.passes, 1));
  const lines = [";;;;", "IN;", "PA;", ...(panel(m, st) ? [] : [`VS${speed};`, `FS${force};`]), "SP1;", "PU;"];
  const [fast, slow] = curveSpeed(st, m), vs = !panel(m, st);
  const L = jobLength(pieces, st, weedBox, margin);
  const R = pieces.length ? edges(pieces, weedBox ? margin / 2 : 0, st, rollWidth) : null;
  let last = 0;
  const travel = vs ? travelSpeed(st, m) : null;
  let pos = [0, 0], curVs = fast;
  const move = (x, y) => {
    if (travel && curVs !== travel && Math.hypot(x - pos[0], y - pos[1]) >= TRAVEL_MIN_IN * upi) { lines.push(`VS${travel};`); curVs = travel; }
    lines.push(`PA${x},${y};`); pos = [x, y];
  };
  const polyline = (p, close, flags) => {
    last = close ? p[0][0] : p[p.length - 1][0];
    move(p[0][0], p[0][1]);
    if (curVs !== fast) { lines.push(`VS${fast};`); curVs = fast; }
    lines.push("PD;");
    const body = close ? [...p.slice(1), p[0]] : p.slice(1);
    let fl = flags ? flags.slice(0, p.length) : p.map(() => false); if (close) fl = [...fl, fl[0]];
    let cur = null;
    body.forEach(([x, y], k) => { const want = vs && slow !== fast && fl[k] ? slow : fast; if (vs && slow !== fast && want !== cur) { lines.push(`VS${want};`); cur = want; } lines.push(`PA${x},${y};`); });
    if (vs && cur !== null && cur !== fast && slow !== fast) lines.push(`VS${fast};`);
    lines.push("PU;");
    pos = close ? p[0] : p[p.length - 1];
  };
  const inward = st.inward_only !== false && !st.drift_safe, fs = signs(st)[0];
  const cut = (p, c, f) => { if (inward && c && p.length > 2) for (const [cp, cf] of inwardChains(p, f, fs, pyRound(OVERCUT_IN * upi))) polyline(cp, false, cf); else polyline(p, c, f); };
  for (let i = 0; i < passes; i++) for (const pc of pieces) for (const [p, c, f] of loopsOf(pc, upi, rollWidth, st, L, R)) cut(p, c, f);
  if (weedBox) polyline(pts(weedBoxPts(pieces, margin), null, upi, rollWidth, st, L, R)[0], false);
  if (st.return_home !== false) { lines.push("PU;"); move(last, 0); }
  if (st.feed_out !== false) { lines.push("PU;"); move(feedOut(pieces, upi, st), 0); }
  const rel = st.relative == null ? Boolean(m.relative) : st.relative;
  return rel ? relative(lines) : lines;
}

// Mirror of backend plotter.inward_chains(): split a closed loop at its film-axis extrema so the knife only cuts while the
// film moves INTO the plotter; outward runs are reversed, runs cut far-end first, junctions overlap by `overlap` units.
export function inwardChains(pts, flags, fs = 1, overlap = 0) {
  const n = pts.length;
  if (n < 3) return [[pts, flags || pts.map(() => false)]];
  const fl = flags && flags.length === n ? [...flags] : pts.map(() => false);
  const key = pts.map((p) => p[0] * fs);
  const sgn = new Array(n).fill(0);
  for (let i = 0; i < n; i++) { const d = key[(i + 1) % n] - key[i]; sgn[i] = d > 0 ? 1 : d < 0 ? -1 : 0; }
  for (let i = 0; i < 2 * n; i++) { const j = i % n; if (sgn[j] === 0) sgn[j] = sgn[(j - 1 + n) % n]; }
  if (sgn.every((v) => v === sgn[0])) return [[pts, fl]];
  let start = 0; while (sgn[start] === sgn[(start - 1 + n) % n]) start++;
  const runs = []; let i = start;
  for (;;) { let j = i; while (sgn[j % n] === sgn[i % n]) j++; runs.push([i % n, j % n, sgn[i % n]]); i = j; if (i % n === start) break; }
  const step = (a, b, dist) => { const dx = b[0] - a[0], dy = b[1] - a[1], l = Math.hypot(dx, dy); return l <= dist || l === 0 ? b : [pyRound(a[0] + dx / l * dist), pyRound(a[1] + dy / l * dist)]; };
  const chains = [];
  for (const [a, b, d] of runs) {
    const idx = [a]; let k = a; while (k !== b) { k = (k + 1) % n; idx.push(k); }
    let P = idx.map((q) => pts[q]), F = [...idx.slice(0, -1).map((q) => fl[q]), false], before, after;
    if (d > 0) { P = P.slice().reverse(); F = []; for (let m = idx.length - 2; m >= 0; m--) F.push(fl[idx[m]]); F.push(false); before = idx[idx.length - 1]; after = idx[0]; }
    else { before = idx[0]; after = idx[idx.length - 1]; }
    if (overlap) {
      P = [step(pts[before], pts[d < 0 ? (before - 1 + n) % n : (before + 1) % n], overlap), ...P, step(pts[after], pts[d < 0 ? (after + 1) % n : (after - 1 + n) % n], overlap)];
      F = [false, ...F, false];
    }
    chains.push([P, F]);
  }
  chains.sort((c1, c2) => c2[0][0][0] * fs - c1[0][0][0] * fs);
  return chains;
}

function relative(lines) {
  const out = []; let cx = 0, cy = 0;
  for (const ln of lines) {
    if (ln === "PA;") out.push("PR;");
    else if (ln.startsWith("PA") && ln.includes(",")) { const [x, y] = ln.slice(2, -1).split(",").map((v) => parseInt(v, 10)); if (x !== cx || y !== cy) out.push(`PR${x - cx},${y - cy};`); cx = x; cy = y; }
    else out.push(ln);
  }
  return out;
}

function genSimple(fmt, pieces, m, st, weedBox, margin, rollWidth) {
  const upi = m.units_per_inch, speed = Math.trunc(num(st.speed, m.default_speed)), force = Math.trunc(num(st.force, m.default_force)), passes = Math.trunc(num(st.passes, 1));
  const g = fmt === "gpgl";
  const lines = g ? [":", "H", ...(panel(m, st) ? [] : [`!${speed}`, `FX${force},1`]), "J1", "L0"] : [";:", "H", "A", "L0", "EC1", ...(panel(m, st) ? [] : [`V${speed}`])];
  const M = (x, y) => (g ? `M${x},${y}` : `U ${x},${y}`), D = (x, y) => (g ? `D${x},${y}` : `D ${x},${y}`), V = (v) => (g ? `!${v}` : `V${v}`);
  const [fast, slow] = curveSpeed(st, m), vs = !panel(m, st);
  const L = jobLength(pieces, st, weedBox, margin);
  const R = pieces.length ? edges(pieces, weedBox ? margin / 2 : 0, st, rollWidth) : null;
  let lastX = 0;
  const travel = vs && !g ? travelSpeed(st, m) : null;
  let pos = [0, 0], curV = fast;
  const up = (x, y) => {
    if (travel && curV !== travel && Math.hypot(x - pos[0], y - pos[1]) >= TRAVEL_MIN_IN * upi) { lines.push(V(travel)); curV = travel; }
    lines.push(M(x, y)); pos = [x, y];
  };
  const down = () => { if (travel && curV !== fast) { lines.push(V(fast)); curV = fast; } };
  for (let i = 0; i < passes; i++) for (const pc of pieces) for (const [p, close, flags] of loopsOf(pc, upi, rollWidth, st, L, R)) {
    lastX = close ? p[0][0] : p[p.length - 1][0];
    up(p[0][0], p[0][1]); down();
    const body = close ? [...p.slice(1), p[0]] : p.slice(1), fl = close ? [...flags, flags[0]] : flags;
    let cur = null;
    body.forEach(([x, y], k) => { const want = vs && slow !== fast && fl[k] ? slow : fast; if (vs && slow !== fast && want !== cur) { lines.push(V(want)); cur = want; } lines.push(D(x, y)); });
    if (vs && cur !== null && cur !== fast && slow !== fast) lines.push(V(fast));
    pos = close ? p[0] : p[p.length - 1];
  }
  if (weedBox) {
    const p = pts(weedBoxPts(pieces, margin), null, upi, rollWidth, st, L, R)[0];
    up(p[0][0], p[0][1]); down();
    if (g) lines.push("D" + p.slice(1).map(([x, y]) => `${x},${y}`).join(",")); else p.slice(1).forEach(([x, y]) => lines.push(D(x, y)));
    pos = p[p.length - 1];
  }
  if (st.return_home !== false) up(lastX, 0);
  if (st.feed_out !== false) up(feedOut(pieces, upi, st), 0);
  const tail = lines[lines.length - 1];
  if (g ? "D!".includes(tail[0]) : (tail.startsWith("D ") || tail.startsWith("V"))) lines.push(g ? M(pos[0], pos[1]) : "U"); // never end blade-down
  if (!g) lines.push("@");
  return lines;
}

// Mirror of plotter.pen_moves()/guard(): replay the finished file and make sure every knife-down move lies on a pattern outline.
const GUARD_TOL_IN = 0.15, PAIR_RE = /-?\d+\s*,\s*-?\d+/g, HAS_PAIR = /-?\d+\s*,\s*-?\d+/;
function* penMoves(commands, fmt) {
  let pos = [0, 0], down = false, rel = false;
  const go = (x, y) => { const a = pos; pos = rel ? [pos[0] + x, pos[1] + y] : [x, y]; return [down, a, pos]; };
  const pairs = (s) => (s.match(PAIR_RE) || []).map((m) => m.split(",").map((v) => parseInt(v, 10)));
  if (fmt === "hpgl") {
    for (const raw of commands.replace(/\n/g, "").split(";")) {
      const cmd = raw.trim(), op = cmd.slice(0, 2).toUpperCase(), args = cmd.slice(2);
      if (op === "IN") { pos = [0, 0]; down = false; rel = false; }
      else if (op === "PA" || op === "PR") rel = op === "PR";
      else if (op === "PU" || op === "PD") down = op === "PD";
      else continue;
      for (const [x, y] of pairs(args)) yield go(x, y);
    }
  } else {
    const up = fmt === "dmpl" ? "U" : "M";
    for (const raw of commands.split(fmt === "dmpl" ? "\n" : "\x03")) {
      const cmd = raw.trim();
      if (cmd[0] === up || cmd[0] === "D") { if (fmt === "gpgl" && !HAS_PAIR.test(cmd)) continue; down = cmd[0] === "D"; for (const [x, y] of pairs(cmd.slice(1))) yield go(x, y); }
      else if (cmd === "H") pos = [0, 0];
    }
  }
  yield [down, pos, null];
}
const segDist = (p, [ax, ay], [bx, by]) => { const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy, t = l2 === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / l2)); return Math.hypot(p[0] - (ax + t * dx), p[1] - (ay + t * dy)); };
export function guardKnife(commands, fmt, loops, upi) {
  const tol = GUARD_TOL_IN * upi, cell = Math.max(1, Math.trunc(upi)), segs = [], grid = new Map();
  for (const [p, close] of loops) {
    const seq = close && p.length ? [...p, p[0]] : p;
    for (let i = 0; i + 1 < seq.length; i++) {
      const a = seq[i], b = seq[i + 1], idx = segs.push([a, b]) - 1;
      for (let cx = Math.floor(Math.min(a[0], b[0]) / cell); cx <= Math.floor(Math.max(a[0], b[0]) / cell); cx++)
        for (let cy = Math.floor(Math.min(a[1], b[1]) / cell); cy <= Math.floor(Math.max(a[1], b[1]) / cell); cy++) { const k = `${cx},${cy}`; if (!grid.has(k)) grid.set(k, []); grid.get(k).push(idx); }
    }
  }
  const onOutline = (p) => {
    const cx = Math.floor(p[0] / cell), cy = Math.floor(p[1] / cell);
    for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) for (const i of grid.get(`${cx + dx},${cy + dy}`) || []) if (segDist(p, segs[i][0], segs[i][1]) <= tol) return true;
    return false;
  };
  const strays = []; let checked = 0;
  for (const [down, a, b] of penMoves(commands, fmt)) {
    if (b === null) { if (down) strays.push({ from: a, to: a, length_in: 0, note: "file ends with the knife down" }); break; }
    if (!down) continue;
    checked++;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]), n = Math.max(1, Math.ceil(len / (0.5 * upi)));
    let ok = true;
    for (let k = 0; k < n && ok; k++) ok = onOutline([a[0] + (b[0] - a[0]) * (k + 0.5) / n, a[1] + (b[1] - a[1]) * (k + 0.5) / n]);
    if (!ok) strays.push({ from: a, to: b, length_in: Math.round(len / upi * 100) / 100 });
  }
  if (strays.length) { const e = new Error(`Safety check stopped this cut: the file would have dragged the knife ${strays[0].length_in}" across the film with the blade down. Nothing was sent to the plotter.`); e.strays = strays; e.checked = checked; throw e; }
  return checked;
}

// pieces: [{world_points, world_holes, knife, knife_holes}] (world coords, inches); machine: profile doc; settings: cut settings + calibration
export function generateCommands(pieces, machine, settings = {}, weedBox = false, margin = 0.5, rollWidth = 36) {
  // a Shadow Cut contour (`anchor` = its pattern's outline) is cut right after the pattern it surrounds
  const key = (p) => { const q = p.anchor || p.world_points; return [Math.min(...q.map((r) => r[1])), Math.min(...q.map((r) => r[0])), p.anchor ? 1 : 0]; };
  const ordered = [...pieces].sort((a, b) => { const ka = key(a), kb = key(b); return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2]; });
  const fmt = machine.command_format;
  const lines = fmt === "hpgl" ? genHpgl(ordered, machine, settings, weedBox, margin, rollWidth) : genSimple(fmt, ordered, machine, settings, weedBox, margin, rollWidth);
  const sep = fmt === "gpgl" ? "\x03" : "\n";
  const commands = lines.join(sep) + (fmt === "gpgl" ? "\x03" : "");
  const upi = machine.units_per_inch, st = settings;
  const L = jobLength(ordered, st, weedBox, margin), R = ordered.length ? edges(ordered, weedBox ? margin / 2 : 0, st, rollWidth) : null;
  const loops = ordered.flatMap((pc) => loopsOf(pc, upi, rollWidth, st, L, R).map(([p, c]) => [p, c]));
  if (weedBox) loops.push([pts(weedBoxPts(ordered, margin), null, upi, rollWidth, st, L, R)[0], false]);
  const knifeDownMoves = guardKnife(commands, fmt, loops, upi);
  return { commands, count: lines.length, knifeDownMoves };
}
